import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { CompanyUserListModel } from '@ninety/platform/models/company-users/company-user-list-model';
import { selectCurrentCompanyUser } from '@ninety/platform/state/app-entities/company-users/company-users-state.selectors';
import { TerraIconModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

import { ConsentComponentStore } from './_state/consent-component.store';
import { ConsentActions } from './_state/consent.actions';

@Component({
  selector: 'ninety-app-integration-consent',
  standalone: true,
  imports: [TerraIconModule, ButtonComponent, LetDirective],
  providers: [ConsentComponentStore],
  templateUrl: './integration-consent.component.html',
  styleUrls: ['./integration-consent.component.scss'],
})
export class IntegrationConsentComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private cs = inject(ConsentComponentStore);
  private store = inject(Store);
  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  clientId = '';
  redirectUri = '';
  state: string | null = null;
  private codeChallenge: string | null = null;
  private codeChallengeMethod: string | null = null;

  /**
   * The whole company-user record is held rather than an id, so the company shown in the label and
   * the user id that goes with it can never disagree — there is no second lookup to miss.
   */
  companyUser: CompanyUserListModel | null = null;

  /**
   * Set when consent cannot be attempted because the company user has not resolved. Rendered in the
   * card so the click is not silently swallowed; cleared on the next attempt that gets far enough.
   */
  errorMsg: string | null = null;

  get selectedCompanyId(): string | null {
    return this.companyUser?.companyId ?? null;
  }

  get userId(): string | null {
    return this.companyUser?.userId ?? null;
  }

  vm$ = this.cs.vm$;
  companyName$ = this.cs.companyName$;
  companyLogo$ = this.cs.companyLogo$;
  loading$ = this.cs.loading$;
  error$ = this.cs.error$;

  ngOnInit(): void {
    this.setRedirectParams();

    this.store.dispatch(ConsentActions.getCompanyDetails({ clientId: this.clientId }));

    // Take once. A company-users refetch replaces every entity (setAll), so a long-lived
    // subscription here would re-emit an identical record and flicker the label for no reason.
    this.store
      .select(selectCurrentCompanyUser)
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(user => (this.companyUser = user));
  }

  private setRedirectParams() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const cid = params.get('client_id');
      const ruri = params.get('redirect_uri');
      const state = params.get('state');
      this.codeChallenge = params.get('code_challenge');
      this.codeChallengeMethod = params.get('code_challenge_method');

      if (cid) {
        this.clientId = cid;
      }
      if (ruri) {
        this.redirectUri = ruri;
      }
      if (state) {
        this.state = state;
      }
    });
  }

  onDeny() {
    this.store.dispatch(ConsentActions.denyIntegration());
    this.router.navigate(['/home']);
  }

  onConsent() {
    const tenantId = this.selectedCompanyId;
    const userId = this.userId;

    // The company user arrives asynchronously from the store, so the button can be clicked before
    // the selector has emitted. Never dispatch a half-populated authorization.
    if (!tenantId) {
      this.errorMsg = 'We could not determine your company. Please reload the page and try again.';
      return;
    }

    if (!userId) {
      this.errorMsg = 'This company is not available for your user. Please switch companies and try again.';
      return;
    }

    this.errorMsg = null;

    this.store.dispatch(
      ConsentActions.authorizeIntegration({
        authorizeIntegrationParams: {
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          state: this.state,
          tenant_id: tenantId,
          user_id: userId,
          ...(this.codeChallenge !== null ? { code_challenge: this.codeChallenge } : {}),
          ...(this.codeChallengeMethod !== null ? { code_challenge_method: this.codeChallengeMethod } : {}),
        },
      })
    );
  }
}
